import { useState, useEffect } from 'react'
import { customerData } from '../../utilities/CustomerData'
import { refreshAccessToken } from '../../utilities/extensions'
import { apiURL } from '../../utilities/URLs'
import { useNavigate, useLocation } from 'react-router-dom'
import { company } from '../../utilities/CustomerData'

export default function ReauthIDVerification() {
  const navigate = useNavigate()
  const location = useLocation()
  const [fetchedRedoSteps, setFetchedRedoSteps] = useState(null)

  useEffect(() => {
    if (location.state?.redoSteps)
      return
    let cancelled = false
    ;(async () => {
      try {
        const fetchRedo = () => fetch(`${apiURL}/api/getReauthRedoSteps?leadId=${encodeURIComponent(customerData.customerLeadData.leadId)}`, {
          headers: { Authorization: customerData.accessToken }
        })
        let res = await fetchRedo()
        let data = await res.json()
        if (data.response?.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          if (refreshedToken && !cancelled) {
            res = await fetchRedo()
            data = await res.json()
          }
        }
        if (!cancelled && data.response && !data.response.error)
          setFetchedRedoSteps(data.response)
      } catch {
        if (!cancelled)
          setFetchedRedoSteps(null)
      }
    })()
    return () => { cancelled = true }
  }, [location.state])

  const redoSteps = location.state?.redoSteps || fetchedRedoSteps

  const [idPhoto, setIdPhoto] = useState(null)
  const [selfie, setSelfie] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [completedReauth, setCompletedReauth] = useState(false)

  const hasRedoFlags = redoSteps && (redoSteps.redoStep1 || redoSteps.redoStep2 || redoSteps.redoStep3)
  const isLastStep = hasRedoFlags && !redoSteps.redoStep2 && !redoSteps.redoStep3

  const getNextRoute = () => {
    const step2Incomplete = redoSteps?.redoStep2 || !redoSteps?.step2Completed
    if (step2Incomplete) return '/reauth/verification-doc'
    return '/reauth/sign-8821'
  }

  useEffect(() => {
    if (redoSteps) {
      const step1Incomplete = redoSteps.redoStep1 || !redoSteps.step1Completed
      if (!step1Incomplete) {
        navigate(getNextRoute(), { state: { redoSteps }, replace: true })
      }
    }
  }, [redoSteps])

  const handleCompleteReauth = async () => {
    try {
      const response = await fetch(`${apiURL}/api/completeReauth8821?leadId=${encodeURIComponent(customerData.customerLeadData.leadId)}`, {
        method: 'POST',
        headers: { 'Authorization': customerData.accessToken }
      })
      const data = await response.json()
      if (data.response.error) {
        if (data.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? handleCompleteReauth() : navigate('/', { state: { expired: true } })
        } else
          setError(data.response.error)
      } else {
        setCompletedReauth(true)
      }
    } catch (err) {
      setError('An error occurred. Please try again.')
    }
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setError('')
    const form = new FormData()
    form.append('idPhoto', idPhoto)
    form.append('selfie', selfie)

    try {
      const response = await fetch(`${apiURL}/api/uploadReauthID?contactId=${encodeURIComponent(customerData.customerLeadData.leadContact.contactId)}`, {
        method: 'POST',
        headers: { 'Authorization': customerData.accessToken },
        body: form
      })
      const data = await response.json()

      if (data.response.error) {
        if (data.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? handleSubmit() : navigate('/', { state: { expired: true } })
        } else
          setError(data.response.error)
      } else {
        if (isLastStep) {
          await handleCompleteReauth()
        } else {
          navigate(getNextRoute(), { state: { redoSteps } })
        }
      }
    } catch (err) {
      setError('An error occurred. Please try again.')
    }
    setSubmitting(false)
  }

  if (completedReauth) {
    return (
      <div className='flex flex-col items-center justify-center min-h-screen bg-gray-50 font-mont p-4'>
        <div className='w-full max-w-lg'>
          <div className='shadow-md p-6 rounded-xl w-full flex flex-col items-center bg-white gap-4'>
            <div className='flex items-center justify-center w-16 h-16 rounded-full bg-green-100'>
              <svg className='w-8 h-8 text-green-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' /></svg>
            </div>
            <p className='text-[#646464] text-center text-lg font-semibold'>Reauthorization Complete!</p>
            <p className='text-[#646464] text-center text-sm'>Thank you for completing all steps. Your account has been reauthorized.</p>
            <button
              onClick={() => window.location.href = '/dashboard'}
              className='px-6 py-3 rounded-lg text-white font-semibold transition-all bg-[#5186C0] hover:bg-[#4070A0]'
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='flex flex-col items-center justify-center min-h-screen bg-gray-50 font-mont p-4'>
      <div className='w-full max-w-lg'>
        <div className='shadow-md p-4 rounded-xl w-full flex flex-col items-center bg-[#5186C0] text-white mb-4'>
          <p className='font-bold text-2xl'>Step 1: ID Verification</p>
          <p className='text-center mt-2'>To verify your identity, please upload a clear photo of your government-issued ID and a selfie.</p>
        </div>
        <div className='shadow-md p-6 rounded-xl w-full flex flex-col items-center bg-white gap-4'>
          <div className='flex flex-col gap-3 w-full'>
            <label className='text-sm font-semibold text-[#646464]'>
              Upload Photo of ID
              <input
                type='file'
                accept='image/*'
                onChange={(e) => setIdPhoto(e.target.files[0])}
                className='mt-1 w-full border border-gray-300 rounded-md p-2'
              />
            </label>
            <label className='text-sm font-semibold text-[#646464]'>
              Upload Selfie
              <input
                type='file'
                accept='image/*'
                onChange={(e) => setSelfie(e.target.files[0])}
                className='mt-1 w-full border border-gray-300 rounded-md p-2'
              />
            </label>
          </div>
          {error && <p className='text-red-500 text-sm'>{error}</p>}
          <button
            onClick={handleSubmit}
            disabled={!idPhoto || !selfie || submitting}
            className={`mt-2 px-6 py-3 rounded-lg text-white font-semibold transition-all w-full ${idPhoto && selfie && !submitting ? 'bg-[#FF8C00] hover:bg-[#E67E00]' : 'bg-gray-400 cursor-not-allowed'}`}
          >
            {submitting ? 'Uploading...' : isLastStep ? 'Submit & Complete' : 'Submit & Continue'}
          </button>
          {submitting && (
            <div className='flex flex-row items-center justify-center gap-2 w-full'>
              <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] h-8 w-8'></div>
              <p className='text-md text-center'>Uploading your ID...</p>
            </div>
          )}
        </div>
        <button
          onClick={() => navigate('/dashboard')}
          className='mt-4 text-[#5186C0] hover:underline text-sm w-full text-center'
        >
          ← Back to Dashboard
        </button>
      </div>
    </div>
  )
}
